import { useEffect, useState } from "react";
import { CalendarHeart, MapPin, CalendarDays } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo, { SITE_URL } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, resolveMedia } from "../lib/api";
import { useLang } from "../i18n";

function EventCard({ ev, i }) {
  const { pick } = useLang();
  return (
    <Reveal i={i % 3}>
      <article data-testid={`event-card-${ev.id}`} className="group h-full bg-white rounded-2xl border border-ngo-deep/10 overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_18px_44px_rgba(23,107,58,0.14)] transition-[box-shadow,transform] duration-300">
        {ev.image ? (
          <img src={resolveMedia(ev.image)} alt={ev.title} loading="lazy" className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-48 bg-ngo-mint flex items-center justify-center text-ngo-deep/40"><CalendarHeart size={36} /></div>
        )}
        <div className="p-6">
          <div className="flex flex-wrap items-center gap-3 text-xs text-ngo-ink/55">
            {ev.date && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(ev.date).toLocaleDateString("hi-IN", { day: "numeric", month: "long", year: "numeric" })}</span>}
            {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {ev.location}</span>}
          </div>
          <h3 className="mt-3 font-heading font-bold text-lg text-ngo-ink leading-snug">{ev.title}</h3>
          {ev.description && <p className="mt-2 text-sm leading-relaxed text-ngo-ink/65">{ev.description}</p>}
          <span className={`mt-4 inline-block px-3.5 py-1.5 rounded-full text-xs font-bold ${ev.status === "upcoming" ? "bg-ngo-deep text-white" : "bg-ngo-ink/10 text-ngo-ink/55"}`}>
            {ev.status === "upcoming" ? pick({ hi: "आगामी", en: "Upcoming" }) : pick({ hi: "संपन्न", en: "Completed" })}
          </span>
        </div>
      </article>
    </Reveal>
  );
}

export default function Events() {
  const { pick } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const upcoming = items.filter((e) => e.status === "upcoming");
  const completed = items.filter((e) => e.status !== "upcoming");
  const eventSchemas = items
    .filter((e) => e.date)
    .map((e) => ({
      "@context": "https://schema.org",
      "@type": "Event",
      name: e.title,
      startDate: e.date,
      eventStatus: e.status === "upcoming" ? "https://schema.org/EventScheduled" : "https://schema.org/EventCompleted",
      location: { "@type": "Place", name: e.location || "Varanasi, Uttar Pradesh" },
      organizer: { "@type": "NGO", name: "Anansha Welfare Society", url: SITE_URL },
      ...(e.image ? { image: e.image.startsWith("http") ? e.image : `${SITE_URL}${e.image}` } : {}),
    }));

  return (
    <main>
      <Seo
        title="हमारे कार्यक्रम — Anansha Welfare Society Events"
        description="Anansha Welfare Society Varanasi के आगामी और संपन्न कार्यक्रम — स्वास्थ्य शिविर, वृक्षारोपण, शिक्षा एवं जागरूकता कार्यक्रम।"
        path="/events"
        breadcrumb={["कार्यक्रम"]}
        jsonLd={eventSchemas.length ? eventSchemas : undefined}
      />
      <PageHero
        overline={{ hi: "कार्यक्रम", en: "Events" }}
        title={{ hi: "हमारे कार्यक्रम", en: "Our Events" }}
        description={{ hi: "संस्था के आगामी और संपन्न कार्यक्रमों की जानकारी।", en: "Information about upcoming and past events of the organization." }}
        testid="events-page-heading"
      />
      <section className="py-20 bg-ngo-cream min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-center text-ngo-ink/50 py-16">...</p>
          ) : items.length === 0 ? (
            <div data-testid="events-empty" className="text-center py-20">
              <CalendarHeart size={40} className="mx-auto text-ngo-deep/40" />
              <p className="mt-4 text-ngo-ink/60">{pick({ hi: "कार्यक्रमों की जानकारी जल्द यहां उपलब्ध होगी।", en: "Event information will be available here soon." })}</p>
            </div>
          ) : (
            <>
              {upcoming.length > 0 && (
                <>
                  <h2 data-testid="events-upcoming-heading" className="font-heading font-bold text-2xl text-ngo-ink mb-7">{pick({ hi: "आगामी कार्यक्रम", en: "Upcoming Events" })}</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
                    {upcoming.map((ev, i) => <EventCard key={ev.id} ev={ev} i={i} />)}
                  </div>
                </>
              )}
              {completed.length > 0 && (
                <>
                  <h2 data-testid="events-completed-heading" className="font-heading font-bold text-2xl text-ngo-ink mb-7">{pick({ hi: "संपन्न कार्यक्रम", en: "Past Events" })}</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {completed.map((ev, i) => <EventCard key={ev.id} ev={ev} i={i} />)}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
}
